<template>
  <section v-if="shouldShow" class="host-financial-alert" :class="{ 'is-expanded': detailsOpen, 'is-open': onboardingOpen, 'has-error': Boolean(error) }">
    <button class="host-financial-alert__summary" type="button" @click="toggleDetails">
      <span class="host-financial-alert__mark"><i class="fas fa-credit-card"></i></span>

      <span class="host-financial-alert__copy">
        <small>{{ eyebrow }}</small>
        <strong>{{ title }}</strong>
        <span>{{ summaryCopy }}</span>
      </span>

      <span class="host-financial-alert__meta">
        <span class="host-financial-alert__status">{{ statusLabel }}</span>
        <i :class="detailsOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </span>
    </button>

    <div v-if="detailsOpen || onboardingOpen || error" class="host-financial-alert__details">
      <div class="host-financial-alert__intro">
        <div>
          <small>{{ introEyebrow }}</small>
          <strong>{{ introTitle }}</strong>
        </div>
        <p>{{ introCopy }}</p>
      </div>

      <div class="host-financial-alert__setup">
        <label class="host-financial-alert__field">
          <span>{{ accountCountryLocked ? 'Pais bloqueado en Stripe' : 'Pais fiscal' }}</span>
          <select v-model="selectedCountry" class="host-financial-alert__select" :disabled="countriesLoading || accountCountryLocked" @change="handleCountryChange">
            <option value="">{{ countryPlaceholder }}</option>
            <option v-for="country in countries" :key="country.code" :value="country.code">{{ country.name }}</option>
          </select>
          <small>{{ countryHelperText }}</small>
        </label>

        <div class="host-financial-alert__info">
          <small>{{ nextStepEyebrow }}</small>
          <p><strong>Cuenta bancaria.</strong> {{ bankAccountCopy }}</p>
          <p><strong>Verificacion.</strong> {{ verificationCopy }}</p>
        </div>
      </div>

      <div v-if="activeEvents.length" class="host-financial-alert__events">
        <small>Eventos como organizador</small>
        <article v-for="event in activeEvents" :key="event.id">
          <strong>{{ event.name || ('Evento #' + event.id) }}</strong>
          <span>{{ eventMeta(event) }}</span>
        </article>
      </div>

      <p v-if="error" class="host-financial-alert__error"><strong>No pudimos abrir Stripe.</strong><span>{{ error }}</span></p>

      <div class="host-financial-alert__actions">
        <button class="host-financial-alert__button host-financial-alert__button--primary" type="button" :disabled="onboardingLoading || !canOpenOnboarding" @click="openOnboarding">
          <i :class="primaryActionIcon"></i>
          <span>{{ onboardingLoading ? 'Abriendo...' : primaryActionLabel }}</span>
        </button>
        <button class="host-financial-alert__button host-financial-alert__button--ghost" type="button" :disabled="loading" @click="loadStatus(true)">
          <span>{{ loading ? 'Revisando...' : 'Revisar estado' }}</span>
        </button>
      </div>

      <div v-if="onboardingOpen" class="host-financial-alert__embed">
        <div class="host-financial-alert__embed-head">
          <div>
            <small>Stripe Express</small>
            <strong>Formulario seguro de cobro</strong>
          </div>
          <span v-if="selectedCountryLabel" class="host-financial-alert__embed-country">{{ selectedCountryLabel }}</span>
        </div>

        <CardPayoutOnboarding
          v-if="clientSecret && publishableKey"
          :client-secret="clientSecret"
          :publishable-key="publishableKey"
          :country="selectedCountryCode"
          @exit="handleOnboardingExit"
          @financial-status="handleFinancialStatus"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'HostFinancialSetupAlert',

  components: {
    CardPayoutOnboarding: defineAsyncComponent(() => import('./CardPayoutOnboarding.vue')),
  },

  props: {
    alwaysVisible: {
      type: Boolean,
      default: false,
    },

    eyebrow: {
      type: String,
      default: 'Cobros con tarjeta',
    },
  },

  data() {
    return {
      status: null,
      loading: false,
      error: '',
      detailsOpen: false,
      onboardingOpen: false,
      onboardingLoading: false,
      clientSecret: '',
      publishableKey: '',
      countries: [],
      countriesLoading: false,
      selectedCountry: '',
    }
  },

  computed: {
    shouldShow() {
      return Boolean(this.status && (this.alwaysVisible || this.status.requires_financial_setup))
    },

    profileStatus() {
      return this.status && this.status.profile ? this.status.profile.status : ''
    },

    profileData() {
      return this.status && this.status.profile ? this.status.profile : {}
    },

    profileCountry() {
      return this.normalizeCountryCode(this.profileData.country)
    },

    hasStripeAccount() {
      return Boolean(this.profileData.account_id)
    },

    accountCountryLocked() {
      return Boolean(this.hasStripeAccount && this.profileCountry)
    },

    needsInitialSetup() {
      return !this.profileData.details_submitted
    },

    profileReady() {
      return Boolean(this.profileData.ready_to_charge_card || this.profileStatus === 'ready')
    },

    activeEventCountryCode() {
      const match = this.activeEvents.find((event) => {
        const country = event && event.country ? event.country : {}
        return this.normalizeCountryCode((event && event.country_code) || country.code)
      })
      if (!match) return ''
      const country = match.country || {}
      return this.normalizeCountryCode(match.country_code || country.code)
    },

    activeEvents() {
      return this.status && Array.isArray(this.status.events) ? this.status.events : []
    },

    selectedCountryCode() {
      return this.accountCountryLocked ? this.profileCountry : this.normalizeCountryCode(this.selectedCountry)
    },

    selectedCountryLabel() {
      const code = this.selectedCountryCode
      if (!code) return ''
      return this.countryName(code) || code
    },

    canOpenOnboarding() {
      return Boolean(this.hasStripeAccount || this.selectedCountryCode)
    },

    title() {
      if (this.profileReady) {
        return 'Stripe Express conectado'
      }
      if (this.profileStatus === 'restricted' && !this.needsInitialSetup) {
        return 'Revisa tus datos bancarios para cobrar con tarjeta'
      }
      if (this.alwaysVisible) {
        return 'Configura tus datos bancarios para cobrar con tarjeta'
      }
      return 'Confirma tus datos bancarios para cobrar con tarjeta'
    },

    summaryCopy() {
      if (this.profileReady) {
        return this.selectedCountryLabel ? `Cuenta lista para transferencias en ${this.selectedCountryLabel}.` : 'Cuenta lista para recibir transferencias.'
      }
      if (this.profileStatus === 'restricted' && !this.needsInitialSetup) {
        return 'Stripe necesita informacion pendiente antes de liberar transferencias.'
      }
      if (this.alwaysVisible) {
        return 'Conecta Stripe Express para recibir transferencias en tu cuenta bancaria.'
      }
      return 'Completa Stripe Express una sola vez para habilitar cobros y transferencias.'
    },

    statusLabel() {
      if (this.profileReady) return 'Activo'
      if (this.needsInitialSetup || this.profileStatus === 'pending') return 'Pendiente'
      if (this.profileStatus === 'restricted') return 'Revision'
      return 'Cuenta host'
    },

    introEyebrow() {
      return this.profileReady ? 'Conexion activa' : 'Antes de abrir Stripe'
    },

    introTitle() {
      if (this.profileReady) {
        return this.selectedCountryLabel ? `Stripe Express esta conectado en ${this.selectedCountryLabel}.` : 'Stripe Express esta conectado.'
      }
      return 'Selecciona el pais fiscal donde vas a registrar tu cuenta bancaria.'
    },

    introCopy() {
      if (this.profileReady) {
        return 'Puedes volver a Stripe para actualizar datos bancarios o completar informacion si Stripe la solicita.'
      }
      return 'Stripe Express pedira banco y solo los documentos que correspondan a ese pais. FOMO no guarda esos datos bancarios.'
    },

    nextStepEyebrow() {
      return this.profileReady ? 'Datos en Stripe' : 'Que sigue'
    },

    primaryActionLabel() {
      return this.profileReady ? 'Gestionar en Stripe' : 'Continuar a Stripe'
    },

    primaryActionIcon() {
      return this.profileReady ? 'fas fa-arrow-up-right-from-square' : 'fas fa-lock'
    },

    bankAccountCopy() {
      if (this.profileReady) {
        return 'Puedes revisar o actualizar el banco de destino dentro de Stripe Express.'
      }
      return 'Stripe te pedira donde transferir el dinero.'
    },

    verificationCopy() {
      if (this.profileReady) {
        return 'Si Stripe necesita informacion adicional, aparecera al gestionar la cuenta.'
      }
      return 'Si hace falta identidad o documento, Stripe lo pedira ahi mismo.'
    },

    countryPlaceholder() {
      if (this.countriesLoading) return 'Cargando paises...'
      return 'Selecciona pais fiscal'
    },

    countryHelperText() {
      if (this.accountCountryLocked) {
        return 'Stripe no permite cambiar el pais despues de crear la cuenta conectada.'
      }
      if (!this.selectedCountryCode) {
        return 'Debe ser el pais fiscal real del organizador o empresa que recibira el dinero.'
      }
      return `Stripe abrira Express para ${this.selectedCountryLabel}.`
    },
  },

  created() {
    this.loadStatus(false)
    this.loadCountries()
  },

  methods: {
    normalizeCollection(payload) {
      if (Array.isArray(payload)) return payload
      if (payload && Array.isArray(payload.results)) return payload.results
      return []
    },

    normalizeCountryCode(value) {
      return String(value || '').trim().toUpperCase()
    },

    normalizeCountries(payload) {
      return this.normalizeCollection(payload).map((country) => ({
        ...country,
        code: this.normalizeCountryCode(country && country.code),
        name: String((country && country.name) || '').trim(),
      })).filter((country) => country.code.length === 2).sort((left, right) => String(left.name || left.code).localeCompare(String(right.name || right.code)))
    },

    countryName(code) {
      const normalizedCode = this.normalizeCountryCode(code)
      const match = this.countries.find((country) => country.code === normalizedCode)
      return match ? match.name : ''
    },

    eventMeta(event) {
      if (!event) return 'Stripe Connect activo'
      const country = event.country || {}
      const countryLabel = country.name || this.countryName(event.country_code || country.code) || event.country_code || country.code || ''
      const slug = event.slug ? `/${event.slug}` : `Evento #${event.id}`
      return [slug, countryLabel, 'Stripe Connect activo'].filter(Boolean).join(' · ')
    },

    resolveErrorMessage(data, fallback) {
      if (!data || typeof data !== 'object') return fallback
      const direct = data.err || data.detail || data.error
      if (typeof direct === 'string' && direct.trim()) return direct
      const firstValue = Object.values(data).find((value) => {
        if (typeof value === 'string') return value.trim()
        return Array.isArray(value) && value.length && typeof value[0] === 'string'
      })
      if (Array.isArray(firstValue)) return firstValue[0]
      return typeof firstValue === 'string' ? firstValue : fallback
    },

    syncSelectedCountry() {
      if (this.accountCountryLocked && this.profileCountry) {
        this.selectedCountry = this.profileCountry
        return
      }
      if (this.selectedCountryCode) {
        this.selectedCountry = this.selectedCountryCode
        return
      }
      if (this.activeEventCountryCode) {
        this.selectedCountry = this.activeEventCountryCode
        return
      }
      this.selectedCountry = ''
    },

    loadCountries() {
      this.countriesLoading = true
      return apiService('/api/countries/', 'GET', undefined, { timeoutMs: 30000 }).then((data) => {
        this.countries = this.normalizeCountries(data)
        this.syncSelectedCountry()
      }).catch(() => {
        this.countries = []
      }).finally(() => {
        this.countriesLoading = false
      })
    },

    loadStatus(refresh = false) {
      this.loading = true
      this.error = ''
      const suffix = refresh ? '?refresh=1' : ''
      return apiService(`/api/connect/financial-status/${suffix}`, 'GET', undefined, { timeoutMs: 30000 }).then((data) => {
        if (!data || data.err || data.detail) {
          return
        }
        this.status = data
        this.syncSelectedCountry()
      }).catch(() => {
      }).finally(() => {
        this.loading = false
      })
    },

    toggleDetails() {
      this.detailsOpen = !this.detailsOpen
    },

    handleCountryChange() {
      this.error = ''
      this.selectedCountry = this.normalizeCountryCode(this.selectedCountry)
    },

    openOnboarding() {
      this.detailsOpen = true
      if (this.onboardingLoading) return

      if (!this.canOpenOnboarding) {
        this.error = 'Selecciona el pais fiscal antes de continuar a Stripe.'
        return
      }

      this.onboardingLoading = true
      this.error = ''
      apiService('/api/connect/account-session/', 'POST', { country: this.selectedCountryCode }, { timeoutMs: 30000 }).then((data) => {
        if (!data || data.err || data.detail || data.error || !data.client_secret || !data.publishable_key) {
          this.error = this.resolveErrorMessage(data, 'No pudimos abrir el formulario de cobro.')
          return
        }
        this.clientSecret = data.client_secret
        this.publishableKey = data.publishable_key
        if (data.financial_status) {
          this.status = data.financial_status
          this.syncSelectedCountry()
        }
        this.onboardingOpen = true
      }).catch(() => {
        this.error = 'No pudimos abrir el formulario de cobro.'
      }).finally(() => {
        this.onboardingLoading = false
      })
    },

    handleFinancialStatus(status) {
      this.status = status
      this.syncSelectedCountry()
    },

    handleOnboardingExit() {
      this.onboardingOpen = false
      this.loadStatus(true)
    },
  },
}
</script>

<style scoped>
.host-financial-alert {
  position: relative;
  display: grid;
  gap: 0;
  overflow: visible;
  border-radius: 28px;
  background:
    radial-gradient(circle at 98% 0%, rgba(105, 84, 135, 0.1), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.012)),
    rgba(7, 7, 12, 0.94);
  border: 1px solid rgba(196, 206, 228, 0.1);
  border-left: 4px solid rgba(214, 223, 240, 0.76);
  box-shadow: 0 22px 52px rgba(0, 0, 0, 0.24), inset 1px 0 0 rgba(255, 255, 255, 0.18);
}

.host-financial-alert::before {
  content: '';
  position: absolute;
  left: -4px;
  top: 18px;
  bottom: 18px;
  width: 4px;
  border-radius: 999px;
  background: linear-gradient(180deg, #f1f4fa, #9da6b5 48%, rgba(156, 133, 184, 0.72));
  box-shadow: 0 0 22px rgba(214, 223, 240, 0.16), 0 0 34px rgba(116, 91, 146, 0.14);
}

.host-financial-alert__summary {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  width: 100%;
  padding: 18px 20px;
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: inherit;
  text-align: left;
}

.host-financial-alert__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 16px;
  color: #f6fbff;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(18, 18, 28, 0.46));
  border: 1px solid rgba(196, 210, 226, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.host-financial-alert__copy {
  display: grid;
  gap: 5px;
}

.host-financial-alert__copy small,
.host-financial-alert__intro small,
.host-financial-alert__field > span,
.host-financial-alert__info small,
.host-financial-alert__events small,
.host-financial-alert__embed-head small {
  color: rgba(201, 214, 230, 0.7);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.host-financial-alert__intro small,
.host-financial-alert__field > span,
.host-financial-alert__info small,
.host-financial-alert__events small {
  color: rgba(188, 199, 216, 0.84);
}

.host-financial-alert__copy strong {
  color: #f6fbff;
  font-family: 'Syne', sans-serif;
  font-size: clamp(1.14rem, 2vw, 1.55rem);
  line-height: 1.02;
  letter-spacing: -0.045em;
}

.host-financial-alert__copy span,
.host-financial-alert__intro p,
.host-financial-alert__field small,
.host-financial-alert__info p {
  margin: 0;
  color: rgba(210, 218, 230, 0.84);
  line-height: 1.5;
}

.host-financial-alert__field small {
  color: rgba(188, 199, 214, 0.86);
}

.host-financial-alert__meta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: rgba(236, 242, 250, 0.9);
}

.host-financial-alert__status,
.host-financial-alert__embed-country {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 11px;
  border-radius: 999px;
  color: #12131a;
  background: linear-gradient(135deg, rgba(242, 246, 251, 0.94), rgba(211, 220, 231, 0.88) 52%, rgba(177, 187, 201, 0.86));
  border: 1px solid rgba(214, 223, 240, 0.3);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.72rem;
  font-weight: 800;
}

.host-financial-alert__details {
  display: grid;
  gap: 12px;
  padding: 0 20px 20px 20px;
}

.host-financial-alert__intro,
.host-financial-alert__setup,
.host-financial-alert__events,
.host-financial-alert__embed {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-radius: 22px;
  background:
    linear-gradient(180deg, rgba(18, 19, 27, 0.86), rgba(10, 11, 16, 0.92));
  border: 1px solid rgba(184, 196, 214, 0.13);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.045);
}

.host-financial-alert__intro {
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  align-items: start;
}

.host-financial-alert__intro strong,
.host-financial-alert__embed-head strong {
  display: block;
  margin-top: 4px;
  color: #f5f8fc;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  letter-spacing: -0.02em;
}

.host-financial-alert__setup {
  grid-template-columns: minmax(220px, 0.9fr) minmax(0, 1.1fr);
}

.host-financial-alert__field {
  display: grid;
  gap: 8px;
}

.host-financial-alert__select {
  width: 100%;
  min-height: 52px;
  padding: 0 16px;
  border-radius: 17px;
  border: 1px solid rgba(198, 210, 226, 0.18);
  outline: 0;
  color: #f7f9fc;
  background:
    linear-gradient(180deg, rgba(28, 30, 39, 0.96), rgba(10, 11, 16, 0.98));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 12px 24px rgba(0, 0, 0, 0.12);
  color-scheme: dark;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.96rem;
  font-weight: 700;
}

.host-financial-alert__select:disabled {
  opacity: 1;
  color: #f3f6fb;
  border-color: rgba(208, 218, 232, 0.18);
  background:
    linear-gradient(180deg, rgba(31, 33, 42, 0.92), rgba(11, 12, 17, 0.96));
}

.host-financial-alert__select option {
  color: #eef6fb;
  background: #0b0d13;
}

.host-financial-alert__info {
  display: grid;
  align-content: start;
  gap: 9px;
}

.host-financial-alert__info strong {
  color: #f7f9fc;
}

.host-financial-alert__events {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.host-financial-alert__events > small {
  grid-column: 1 / -1;
}

.host-financial-alert__events article {
  display: grid;
  gap: 4px;
  padding: 12px 13px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(196, 210, 226, 0.11);
}

.host-financial-alert__events strong {
  color: #f7f9fc;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.95rem;
}

.host-financial-alert__events span {
  color: rgba(210, 218, 230, 0.76);
  font-size: 0.86rem;
  line-height: 1.4;
}

.host-financial-alert__error {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 13px 15px;
  border-radius: 18px;
  color: #ffd7d7;
  background: rgba(132, 32, 48, 0.16);
  border: 1px solid rgba(255, 188, 198, 0.16);
}

.host-financial-alert__error strong {
  color: #fff1f1;
}

.host-financial-alert__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.host-financial-alert__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 46px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1px solid rgba(196, 210, 226, 0.14);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(18, 18, 28, 0.32));
  color: #eef4ff;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.9rem;
  font-weight: 800;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.host-financial-alert__button--primary {
  color: #12131a;
  background: linear-gradient(135deg, rgba(242, 246, 251, 0.94), rgba(211, 220, 231, 0.88) 52%, rgba(177, 187, 201, 0.86));
  border-color: rgba(214, 223, 240, 0.3);
}

.host-financial-alert__button--ghost {
  background: rgba(11, 10, 18, 0.72);
}

.host-financial-alert__button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.host-financial-alert__embed {
  background: linear-gradient(180deg, rgba(11, 10, 18, 0.86), rgba(6, 7, 12, 0.92));
}

.host-financial-alert__embed-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
}

@media (max-width: 820px) {
  .host-financial-alert__summary,
  .host-financial-alert__intro,
  .host-financial-alert__setup {
    grid-template-columns: 1fr;
  }

  .host-financial-alert__meta,
  .host-financial-alert__actions {
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .host-financial-alert {
    border-radius: 24px;
  }

  .host-financial-alert__summary {
    padding: 16px;
  }

  .host-financial-alert__details {
    padding: 0 16px 16px;
  }

  .host-financial-alert__button,
  .host-financial-alert__actions,
  .host-financial-alert__embed-head {
    width: 100%;
  }

  .host-financial-alert__embed-head {
    grid-template-columns: 1fr;
  }
}
</style>
